import React, { useContext } from "react";
import styled from "styled-components";
import { generatePath } from "react-router-dom";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { AFFILIATION_PATH } from "common/constants";
import { NEW_LOOK_GAP } from "common/NewLook/tokens.ts";
import { AffiliationContext } from "features/affiliation/Affiliation.context";
import useReferralLinks from "features/affiliation/common/hooks/useReferralLinks";
import useReferredUsers from "features/affiliation/common/hooks/useReferredUsers";
import {
  AffiliationTabs,
  ExistingUsersStatistics,
  ReferralLinksStatistics,
} from "features/affiliation/common/types";
import OverviewHero from "features/affiliation/NewLook/OverviewHero";
import ReferredUsersPanel from "features/affiliation/NewLook/ReferredUsersPanel";
import TierPanel from "features/affiliation/NewLook/TierPanel";
import FiguresPanel from "features/affiliation/NewLook/FiguresPanel";
import { ChangeMeta } from "features/affiliation/NewLook/roles";
import {
  AFFILIATION_CONTAINER,
  formatShare,
  formatUsd,
  toNumber,
} from "features/affiliation/NewLook/newLookUtils";

/**
 * The affiliation overview in the new look, composed like the dashboard:
 * one hero (total earnings and where the money is), then new referred users
 * beside the commission tier, then the link funnel beside existing users.
 */
const NewLookOverview = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { isAdmin, hasFeatureFlag } = useContext(AffiliationContext)!;
  const { getStatisticValue: getLinksValue } = useReferralLinks();
  const {
    getStatisticTotalValue,
    getStatisticLastMonthValue,
    referralHistoryQuery,
  } = useReferredUsers(AffiliationTabs.EXISTING_USERS);
  const seeAllLabel = t("common.see_all");
  const tabPath = (tab: AffiliationTabs) =>
    generatePath(AFFILIATION_PATH, { tab });

  const clicks = getLinksValue(ReferralLinksStatistics.TOTAL_CLICKS) || 0;
  const referred = getLinksValue(ReferralLinksStatistics.REFERRED_USERS) || 0;
  const paid = getLinksValue(ReferralLinksStatistics.PAID_USERS) || 0;
  const linkItems = [
    {
      key: "clicks",
      label: t(ReferralLinksStatistics.TOTAL_CLICKS),
      value: clicks.toLocaleString("en-US"),
    },
    {
      key: "referred",
      label: t(ReferralLinksStatistics.REFERRED_USERS),
      value: referred.toLocaleString("en-US"),
      meta:
        clicks > 0
          ? t("overview.share_of_clicks", {
              percent: formatShare(referred / clicks),
            })
          : undefined,
    },
    {
      key: "paid",
      label: t(ReferralLinksStatistics.PAID_USERS),
      value: paid.toLocaleString("en-US"),
      meta:
        referred > 0
          ? t("overview.share_of_referred", {
              percent: formatShare(paid / referred),
            })
          : undefined,
    },
  ];

  const existingUsersItems = Object.values(ExistingUsersStatistics).map(
    (title) => {
      const isMoney = title === ExistingUsersStatistics.TOTAL_EARNED;
      const total = toNumber(getStatisticTotalValue(title));
      const lastMonth = toNumber(getStatisticLastMonthValue(title));
      return {
        key: title,
        label: t(title),
        value: isMoney ? formatUsd(total) : total.toLocaleString("en-US"),
        meta: !referralHistoryQuery.isLoading && (
          <ChangeMeta
            value={lastMonth}
            formatted={`+${isMoney ? formatUsd(lastMonth) : lastMonth}`}
            suffix={t("common.from_last_month")}
          />
        ),
      };
    }
  );

  return (
    <Container data-testid="affiliation-overview">
      <OverviewHero />
      <Columns>
        <ReferredUsersPanel withSeeAll={true} />
        <TierPanel />
      </Columns>
      <Columns $single={!isAdmin}>
        <FiguresPanel
          title={t("overview.links")}
          seeAllTo={
            hasFeatureFlag ? tabPath(AffiliationTabs.REFERRAL_LINKS) : undefined
          }
          seeAllLabel={seeAllLabel}
          items={linkItems}
        />
        {isAdmin && (
          <FiguresPanel
            title={t("tabs.existing_users")}
            seeAllTo={tabPath(AffiliationTabs.EXISTING_USERS)}
            seeAllLabel={seeAllLabel}
            items={existingUsersItems}
          />
        )}
      </Columns>
    </Container>
  );
};

export default NewLookOverview;

const Container = styled.div`
  container: ${AFFILIATION_CONTAINER} / inline-size;
  display: flex;
  flex-direction: column;
  gap: ${NEW_LOOK_GAP}px;
  /* Phones: the 12px rhythm of the phone page. */
  @media (max-width: 600px) {
    gap: 12px;
  }
`;
const Columns = styled.div<{ $single?: boolean }>`
  display: grid;
  grid-template-columns: ${(p) =>
    p.$single ? "minmax(0, 1fr)" : "minmax(0, 1.65fr) minmax(0, 1fr)"};
  gap: ${NEW_LOOK_GAP}px;
  align-items: stretch;
  @container ${AFFILIATION_CONTAINER} (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
  @media (max-width: 600px) {
    gap: 12px;
  }
`;
